import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Container from '../components/ui/Container';
import BlogSearch from '../components/BlogSearch';
import Reveal from '../components/motion/Reveal';
import OptimizedImage from '../components/ui/OptimizedImage';
import './BlogListPage.css';

const BlogListPage = () => {
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let isMounted = true;
    fetch('/blog/index.json', { cache: 'no-store' })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load blog index');
        return res.json();
      })
      .then((data) => {
        if (!isMounted) return;
        const sorted = [...data].sort((a, b) => new Date(b.date) - new Date(a.date));
        setPosts(sorted);
      })
      .catch((err) => {
        if (!isMounted) return;
        setError(err.message || 'Error loading blog posts');
      })
      .finally(() => isMounted && setIsLoading(false));
    return () => {
      isMounted = false;
    };
  }, []);

  const blogListStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    name: 'PySolutions Blog',
    description: 'Insights on Python, AI, and software engineering from PySolutions.',
    url: 'https://pysolutions.dev/blog',
    blogPost: posts.slice(0, 10).map((post) => ({
      '@type': 'BlogPosting',
      headline: post.title,
      description: post.description,
      url: `https://pysolutions.dev/blog/${post.slug}`,
      datePublished: post.date,
      author: { '@type': 'Organization', name: post.author || 'PySolutions' }
    }))
  };

  return (
    <div className="blog-list-page page-with-header">
      <Helmet>
        <title>Engineering Blog — AI agents, RAG, Next.js + Python | PySolutions</title>
        <meta name="description" content="Senior engineering notes on AI agents, RAG systems, modern backends, Next.js + Python, cloud, and DevOps — the production patterns we ship." />
        <link rel="canonical" href="https://pysolutions.dev/blog" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://pysolutions.dev/blog" />
        <meta property="og:title" content="Engineering Blog — AI agents, RAG, Next.js + Python | PySolutions" />
        <meta property="og:description" content="Senior engineering notes on AI agents, RAG systems, modern backends, Next.js + Python, cloud, and DevOps — the production patterns we ship." />
        <meta property="og:image" content="https://pysolutions.dev/og-card.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="PySolutions Engineering Blog" />
        <meta property="og:site_name" content="PySolutions" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Engineering Blog — AI agents, RAG, Next.js + Python | PySolutions" />
        <meta name="twitter:description" content="Senior engineering notes on AI agents, RAG systems, modern backends, Next.js + Python, cloud, and DevOps — the production patterns we ship." />
        <meta name="twitter:image" content="https://pysolutions.dev/og-card.jpg" />
        <script type="application/ld+json">{JSON.stringify(blogListStructuredData)}</script>
      </Helmet>

      <section className="blog-hero section">
        <div className="blog-hero-background">
          <div className="hero-glow hero-glow-1"></div>
          <div className="hero-glow hero-glow-2"></div>
        </div>
        <Container>
          <div className="blog-hero-content">
            <div className="hero-badge"><span className="badge-icon">📝</span> PySolutions Blog</div>
            <h1 className="hero-title">Engineering insights and <span className="logo-highlight">Python that ships</span></h1>
            <p className="hero-description">Short, practical reads on Python, AI, architecture, and performance.</p>
            <div className="hero-actions">
              <Link to="/blog/tags" className="btn-secondary">
                <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
                Browse by Tags
              </Link>
            </div>
          </div>
        </Container>
      </section>

      <section className="blog-list section">
        <Container>
          {isLoading && (
            <div className="loading"><div className="spinner"></div></div>
          )}
          {error && (
            <div className="error">{error}</div>
          )}
          {!isLoading && !error && (
            <>
              <BlogSearch posts={posts} />
              <div className="blog-grid">
              {posts.map((post, index) => (
                <Reveal key={post.slug} className="blog-card-reveal" delay={(index % 3) * 0.06} y={20}>
                <article className="blog-card glass-card">
                  <Link to={`/blog/${post.slug}`} className="blog-card-link">
                    <div className="blog-card-media">
                      <OptimizedImage 
                        src={post.image || '/PySolutions.jpeg'} 
                        alt={`${post.title} - PySolutions Python development article covering enterprise solutions and best practices`}
                        width="400"
                        height="225"
                        loading="lazy"
                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                      />
                    </div>
                    <div className="blog-card-content">
                      <div className="blog-card-meta">
                        <span className="badge">{new Date(post.date).toLocaleDateString()}</span>
                      </div>
                      <h2 className="blog-card-title">{post.title}</h2>
                      <p className="blog-card-desc">{post.description}</p>
                      <div className="blog-card-footer">
                        <span className="read-more">Read more</span>
                        <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5-5 5M6 12h12" />
                        </svg>
                      </div>
                    </div>
                  </Link>
                </article>
                </Reveal>
              ))}
              </div>
            </>
          )}
        </Container>
      </section>
    </div>
  );
};

export default BlogListPage; 